import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Environment, getEnvironment } from '../../../../../common/src/environment.ts';
import {
  clefForVoicePart,
  currentStreak,
  learnLevelName,
  learnListeningTunables,
  learnModuleName,
  moduleProgress,
  pitchMatchTunables,
  RecordedSession,
  recordLearnSession,
  voicePartForRange,
} from '../../../../../common/src/learn_rules.ts';
import { LearnLevel, LearnModule, LearnProgress, VocalRange } from '../../../../../common/src/model.ts';
import { getLearnPath, LearnPages } from '../../../../../common/src/pages.ts';
import { pickPitchTargets } from '../../../../../common/src/pitch_match.ts';
import { RandomSource } from '../../../../../common/src/rhythm_echo.ts';
import { LocalStorageKey } from '../../../data/client_local_storage.ts';
import { LearnProgressUpdate, useLearnProgress } from '../../../data/use_learn_progress.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { OuterPageContent } from '../../../util/shared.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Header } from '../../header.tsx';
import { getLearnAudioSession, LearnAudioSession } from '../audio/learn_audio_session.ts';
import { MicFailure, micFailureOf } from '../audio/learn_mic_capture.ts';
import { useAudioClock } from '../audio/use_audio_clock.ts';
import { learnModuleBack } from '../learn_classes.ts';
import { nowOn } from '../learn_dates.ts';
import { failureScreenIds, SingingFailureScreen } from '../learn_failure_screen.tsx';
import { learnHandedOnState, useLearnExit, useLearnFrom } from '../learn_navigation.tsx';
import { LearnSummaryScreen, summaryScreenIds } from '../learn_summary_screen.tsx';
import {
  actionTimeMs,
  captureActionsKeepMs,
  PitchCaptureEntry,
  savePitchCapture,
  trimCaptureLog,
} from './pitch_capture.ts';
import {
  bestStreak,
  initialPitchSessionState,
  isGuideRinging,
  PitchMode,
  pitchNoteIndex,
  PitchSessionAction,
  PitchSessionState,
  reducePitchSession,
  sessionScore,
  summarySquares,
} from './pitch_match_session.ts';
import { defaultSingAlong, pitchModeFor } from './pitch_mode.ts';
import { PitchCaptureStatus, PitchNoteScreen } from './pitch_note_screen.tsx';
import { PitchStartScreen } from './pitch_start_screen.tsx';
import { drawableMidiRange } from './staff_geometry.ts';

// Pitch Match (issue #3518): the page behind the hub's Pitch card. The start screen (level, the
// sing-along switch, Start), then one screen per note of a ten-note session - the staff, the hint
// and the hold meter - and the shared summary. No footer; the header's back button returns to the
// Pitch class screen (issue #3719), stepping back to it when it is the entry before
// (learn_navigation.tsx), and leaving mid-session records nothing.
//
// The range gate. Every target is drawn from the singer's measured range, intersected with the
// notes their clef's staff can draw, so a member without a range is sent to the Range Finder. The
// Pitch class screen's tile already sends them there; this covers a direct link and a tab left open
// from before the range was cleared. The redirect takes this entry's place and hands on where the
// singer came from, so the Range Finder's way back returns there, and that they came for Pitch
// Match, so its way on leads back here (learn_navigation.tsx).
//
// Audio. The Start tap is the user gesture: it opens the mic on the shared Learn audio session
// (which resumes the one long-lived context), and from then on the session's frames drive the
// state machine in pitch_match_session.ts. Each new guide request aims the known-target estimator
// at the note, schedules the piano note one lead ahead - the session silences whatever piano note
// the guide before it left in the air, so one note is in the air at a time without the page
// cancelling anything - and, once it is scheduled (a soundfont still loading delays it, and one
// that cannot be loaded at all resolves without a sound rather than never), tells the machine the
// time it was really anchored at, so play-then-listen knows what to ignore and both modes know how
// long the reference will ring. The note is scheduled for as long as the mode
// wants it: the piano's whole ring in sing-along, so it sounds once and is still there while they
// hunt for it, and guideMs in play-then-listen, where they answer it after it stops. A lock rings
// the chime. Reaching the summary records the session once, ends the mic and suspends the context;
// leaving the page does the same without recording. The context is never closed.
//
// Time. Every transition comes from the frames' audio-clock times and from the audio clock polled
// on requestAnimationFrame (the lock and miss pauses, and the guide's own ring), never from a
// timer, so a story that injects the fake session, moves its clock and emits frames drives the
// page through exactly the transitions a real microphone would.
//
// Injection. `audio` defaults to the page-wide Learn audio session and `random` to Math.random;
// stories and tests pass the fake session and a seeded source. `initialState` opens on a session
// built by hand; the real page always starts on the start screen, because its tap is the gesture
// that opens the mic.
//
// Capturing a flicker (issue #3587). On a dev server the page turns the session's rolling capture
// on for as long as it is mounted, and off again as it leaves, so the Range Finder and the hub
// never record. It also logs every action it dispatches to the machine, with the note that action
// was scored against, trimmed to the last 40 s. Once there is audio kept, the note screen offers
// "Save last 30 s", which uploads the audio and the log to the dev server (pitch_capture.ts), so a
// flicker seen on a phone can be replayed through the same machine on the developer's machine.
// Production never logs, never records and never shows the link; a story never has audio to save.

export const pitchMatchPageId = 'pitch-match-page';

// "Back to Pitch" and the Pitch class screen it leads to (issue #3719)
// resolved on use, not at import: a slip in learn_classes.ts must not take the whole app down
const pitchMatchBack = () => learnModuleBack(LearnModule.PitchMatch);

// The exercise's own prefix for the shared summary and failure screens, and the ids they render
// from it, so the page, those screens and the stories name the same strings (issue #3518)
const pitchIdPrefix = 'pitch';
const summaryIds = summaryScreenIds(pitchIdPrefix);
export const pitchSummaryScreenId = summaryIds.screen;
export const pitchSummaryScoreTestId = summaryIds.score;
export const pitchSummarySquareTestId = summaryIds.square;
export const pitchSummaryUnlockedTestId = summaryIds.unlocked;
export const pitchPlayAgainButtonId = summaryIds.playAgain;
export const pitchBackToLearnButtonId = summaryIds.backToLearn;
const failureIds = failureScreenIds(pitchIdPrefix);
export const pitchFailureScreenId = failureIds.screen;
export const pitchRetryButtonId = failureIds.retry;
export const pitchFailureBackButtonId = failureIds.back;

export const guideLeadMs = 100; // from a guide request to the note, so the scheduled time is never already past

// Only a dev server has the sink a capture is saved to (server/src/audio_lab_routes.ts)
const captureAvailable = getEnvironment() === Environment.Dev;

// How often the action log is trimmed, in actions: often enough that it never grows far past its
// window, rarely enough that a frame costs nothing
const captureTrimEvery = 100;

// How long "Saved to your Mac" or "Save failed" stays beside the link
const captureStatusShowMs = 4000;

// The levels this build can play; Medium and Hard wait for their own slice
const selectableLevels: LearnLevel[] = [LearnLevel.Easy];

interface Props {
  audio?: LearnAudioSession;
  random?: RandomSource;
  initialLevel?: LearnLevel;
  initialMode?: PitchMode; // the device default a story pretends this device has; a stored choice still wins
  initialState?: PitchSessionState; // opens on the session, on this state, with its targets as they are
}

export const PitchMatchPage = (props: Props) => {
  const { progress, updateProgress, flush } = useLearnProgress();
  const from = useLearnFrom();
  const range = progress.range;

  // No measured range, no targets to draw: the Range Finder comes first (issue #3481)
  if (!range) {
    return (
      <Navigate
        to={getLearnPath(LearnPages.Range)}
        replace
        state={learnHandedOnState(from, getLearnPath(LearnPages.PitchMatch))}
      />
    );
  }

  return (
    <PitchMatchExercise {...props} range={range} progress={progress} updateProgress={updateProgress} flush={flush} />
  );
};

// The session state the screens read, or undefined until the Start tap opens one. Every action but
// `open` belongs to the pure reducer; `open` is the page's own, because a session is built outside
// the machine, from the level and mode the start screen chose and a fresh draw of targets. Both
// Start and "Play again" dispatch it (beginSession below).
type PitchPageAction = PitchSessionAction | { type: 'open'; state: PitchSessionState };

function reducePitchPage(state: PitchSessionState | undefined, action: PitchPageAction): PitchSessionState | undefined {
  if (action.type === 'open') {
    return action.state;
  }
  return state && reducePitchSession(state, action);
}

const PitchMatchExercise = ({
  audio: audioProp,
  random: randomProp,
  initialLevel,
  initialMode,
  initialState,
  range,
  progress,
  updateProgress,
  flush,
}: Props & {
  range: VocalRange;
  progress: LearnProgress;
  updateProgress: (update: LearnProgressUpdate) => void;
  flush: () => Promise<void>;
}) => {
  const audio = audioProp ?? getLearnAudioSession();
  const random = randomProp ?? Math.random;
  const today = useCurrentDate();
  const [level, setLevel] = useState(initialState?.level ?? initialLevel ?? LearnLevel.Easy);
  const [singAlongChoice, setSingAlongChoice] = useLocalStorage(LocalStorageKey.PitchMatchSingAlong);
  const [recorded, setRecorded] = useState<RecordedSession | undefined>(undefined);
  const singAlong = singAlongChoice ?? (initialMode ? initialMode === 'singAlong' : defaultSingAlong());
  const mode = pitchModeFor(singAlong);
  const { unlocked } = moduleProgress(progress, LearnModule.PitchMatch);
  const clef = clefForVoicePart(progress.voicePart ?? voicePartForRange(range));

  // The notes targets are drawn from: the measured range, narrowed to what this clef's staff can
  // draw, so a target the staff would have to clamp is never asked for (a tenor's A4 is on the bass
  // staff, a soprano's high C on the treble one; anything past the band is not). pitchTargetBounds
  // falls back on its own when what is left is too narrow to run a session on.
  const targetRange: VocalRange = useMemo(() => {
    const drawable = drawableMidiRange(clef);
    return {
      lowMidi: Math.max(range.lowMidi, drawable.lowMidi),
      highMidi: Math.min(range.highMidi, drawable.highMidi),
    };
  }, [clef, range]);

  // undefined is the start screen: the real page waits there for the Start tap, which is the
  // gesture that opens the mic; a story hands in the session it wants to open on
  const [state, rawDispatch] = useReducer(reducePitchPage, initialState);

  const mountedRef = useRef(true);
  const handledRequestRef = useRef<number | undefined>(undefined);
  const chimedNoteRef = useRef<number | undefined>(undefined);
  const recordedRef = useRef(false);
  const stateRef = useRef(state); // the latest committed state, which the capture log reads (see dispatch)
  const captureLogRef = useRef<PitchCaptureEntry[]>([]);
  const actionsSinceTrimRef = useRef(0);
  const captureStatusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [captureStatus, setCaptureStatus] = useState<PitchCaptureStatus | undefined>(undefined);

  useEffect(() => {
    stateRef.current = state;
  });

  // Every action goes to the machine; on a dev server each one the machine handles is logged too,
  // against the note on screen when it was dispatched, so a saved capture can be replayed. Refs
  // only, so the function is as stable as the reducer's own dispatch.
  //
  // `open` is the page's own and is not logged, but the session it opens is what the actions after
  // it are scored against, before a render has committed it: the "micFailed" beginSession
  // dispatches straight after it, and the frames of the moment in between.
  const dispatch = useCallback((action: PitchPageAction) => {
    rawDispatch(action);
    if (action.type === 'open') {
      stateRef.current = action.state;
      return;
    }
    const current = stateRef.current;
    if (!captureAvailable || !current) {
      return;
    }
    captureLogRef.current.push({ action, noteIndex: pitchNoteIndex(current), targetMidi: current.note.targetMidi });
    actionsSinceTrimRef.current += 1;
    const timeMs = actionTimeMs(action);
    if (actionsSinceTrimRef.current < captureTrimEvery || timeMs === undefined) {
      return;
    }
    actionsSinceTrimRef.current = 0;
    captureLogRef.current = trimCaptureLog(captureLogRef.current, timeMs, captureActionsKeepMs);
  }, []);

  // Every analysed frame goes to the state machine for the page's life
  useEffect(() => audio.onFrame((frame) => dispatch({ type: 'frame', frame })), [audio, dispatch]);

  // Leaving the page unaims the detector, silences anything pending, ends the mic track and
  // suspends the context - all of it inside the session's own leave(), which guards each step of
  // the microphone teardown so that a guide note is never left sounding on the hub behind the
  // singer. A session abandoned here records nothing. The rolling capture is this page's alone: on
  // while it is mounted on a dev server, and off before the next exercise can open the mic.
  useEffect(() => {
    mountedRef.current = true;
    audio.setCaptureEnabled(captureAvailable);
    return () => {
      mountedRef.current = false;
      clearTimeout(captureStatusTimerRef.current);
      audio.setCaptureEnabled(false);
      audio.leave();
    };
  }, [audio]);

  // The guide follows the state: each new request aims the detector, plays the note on the piano
  // and reports when it sounded. The ref keeps StrictMode's doubled effects from playing it twice.
  const requestId = state?.phase === 'note' ? state.note.guideRequestId : undefined;
  const guideMidi = state?.phase === 'note' ? state.note.targetMidi : undefined;
  // Sing-along lets the note ring on while they sing; play-then-listen cuts it and then listens
  const guideDurationMs =
    (state?.mode ?? mode) === 'singAlong' ? pitchMatchTunables.singAlongGuideMs : learnListeningTunables.guideMs;
  useEffect(() => {
    if (requestId === undefined || guideMidi === undefined) {
      handledRequestRef.current = undefined;
      audio.setTargetMidi(undefined);
      return;
    }
    if (handledRequestRef.current === requestId) {
      return;
    }
    handledRequestRef.current = requestId;
    // One guide at a time. The note asked for before this one may still be ringing (the sing-along
    // guide rings for eight seconds, and "Play again" asks for another inside that), and two
    // pianos on two notes is neither a reference nor a thing to sing against. The session enforces
    // that itself - schedulePianoNote stops any piano note still pending, ringing or waiting on the
    // soundfont - so the page asks for the note and nothing else, and the lock chime of the note
    // before is left to ring out (issue #3518). The reducer's own guard still swallows a "Play
    // again" tapped while a request is in flight.
    audio.setTargetMidi(guideMidi);
    const atMs = audio.nowMs() + guideLeadMs;
    void audio.schedulePianoNote(guideMidi, atMs, guideDurationMs).then((startedMs) => {
      if (!mountedRef.current) {
        return;
      }
      // The time the note was really anchored at, not the time it was asked for: after a slow
      // soundfont load the two differ, and everything the machine measures from the guide - the
      // listening window and the ring the "Play again" button reads - has to be measured from what
      // the singer actually heard
      dispatch({ type: 'guideStarted', requestId, atMs: startedMs });
    });
  }, [audio, dispatch, requestId, guideMidi, guideDurationMs]);

  // A locked note rings once, as it locks: the ref is the index of the note already rung
  const lockedNoteIndex =
    state?.phase === 'note' && state.note.outcome?.kind === 'locked' ? pitchNoteIndex(state) : undefined;
  useEffect(() => {
    if (lockedNoteIndex === undefined || chimedNoteRef.current === lockedNoteIndex) {
      return;
    }
    chimedNoteRef.current = lockedNoteIndex;
    audio.scheduleChime(audio.nowMs());
  }, [audio, lockedNoteIndex]);

  // Ticks drive the lock and miss pauses and the guide's own ring, and nothing else: while a note
  // is simply being sung every transition comes from the frames themselves. So the clock is polled
  // only while an outcome is waiting out its pause or the guide is actually ringing - the latter so
  // that "Play again" turns solid the moment the piano fades even in a quiet room, where no frame
  // may arrive to carry the time. A guide merely asked for is not enough (isGuideRinging, not
  // isGuideSounding): there is nothing to time yet, and a cold soundfont would otherwise be waited
  // out at sixty polls a second. The summary and the failure screen never poll.
  const clockRunning = state?.phase === 'note' && (state.note.outcome !== undefined || isGuideRinging(state));
  useAudioClock(audio, clockRunning, (nowMs) => dispatch({ type: 'tick', nowMs }));

  // The summary is the one point that records the session, on the transition into it. A layout
  // effect, so the summary's very first paint already carries the best-score and unlock flags
  // rather than painting once without them.
  //
  // The session is recorded inside the updater and the result is reused for the screen:
  // useLearnProgress invokes the updater synchronously (see use_learn_progress.tsx), so `recorded`
  // is set by the time updateProgress returns, and what the summary shows is exactly what was
  // stored. Recording it a second time, against this render's `progress`, would be a second answer
  // to the same question and the two could disagree. With no active member to record for the
  // updater never runs and nothing is stored; the summary then shows what the session would have
  // been, which is the only thing left to show.
  useLayoutEffect(() => {
    if (state?.phase !== 'summary' || recordedRef.current) {
      return;
    }
    recordedRef.current = true;
    const session = {
      module: LearnModule.PitchMatch,
      level: state.level,
      score: sessionScore(state.results),
      now: nowOn(today),
    };
    let stored: RecordedSession | undefined;
    updateProgress((current) => {
      stored = recordLearnSession({ progress: current, ...session });
      return stored.progress;
    });
    // The recorded session is what the summary screen reads; nothing else can produce its flags.
    setRecorded(stored ?? recordLearnSession({ progress, ...session }));
    void flush();
    audio.leave(); // the session is over: the same teardown as leaving the page
  }, [audio, flush, progress, state, today, updateProgress]);

  // Out of the exercise, by the header's arrow or the summary's and failure screen's button: back
  // to the Pitch class screen (issue #3719)
  const onBack = useLearnExit(pitchMatchBack().path);

  // The gesture that opens the mic, reporting a refusal instead of throwing: a session that cannot
  // listen still opens, on the failure screen, so Try again resumes the note it was to start on
  const openMic = useCallback(async (): Promise<MicFailure | undefined> => {
    try {
      await audio.openMic();
      return undefined;
    } catch (error) {
      return micFailureOf(error);
    }
  }, [audio]);

  // The one way into a session, behind both Start and "Play again": the mic first (the tap is the
  // gesture that opens it), then a session on the chosen level and mode with a fresh draw of
  // targets, and the failure screen if the mic refused. "Play again" needs nothing of its own -
  // neither the level nor the mode can change mid-session, and the guide request ids need not
  // carry across sessions, because the summary the new session replaces left the guide effect with
  // no request to hand (its handled ref is cleared whenever the phase is not 'note').
  const beginSession = useCallback(async () => {
    const failure = await openMic();
    if (!mountedRef.current) {
      return;
    }
    chimedNoteRef.current = undefined;
    recordedRef.current = false;
    setRecorded(undefined);
    captureLogRef.current = [];
    actionsSinceTrimRef.current = 0;
    const targets = pickPitchTargets(targetRange, level, random);
    dispatch({ type: 'open', state: initialPitchSessionState({ level, mode, targets }) });
    if (failure) {
      dispatch({ type: 'micFailed', failure });
    }
  }, [dispatch, level, mode, openMic, random, targetRange]);

  // Try again: a mic that would not open is opened again from this tap; a noisy room only resumes
  const onRetry = useCallback(async () => {
    if (state?.phase === 'failed' && state.failure !== 'tooNoisy') {
      const failure = await openMic();
      if (!mountedRef.current) {
        return;
      }
      if (failure) {
        dispatch({ type: 'micFailed', failure });
        return;
      }
    }
    dispatch({ type: 'retry' });
  }, [dispatch, openMic, state]);

  const onReplay = useCallback(() => dispatch({ type: 'replay' }), [dispatch]);
  const onSkip = useCallback(() => dispatch({ type: 'skip', nowMs: audio.nowMs() }), [audio, dispatch]);
  const onToggleSingAlong = useCallback((value: boolean) => setSingAlongChoice(value), [setSingAlongChoice]);

  // "Save last 30 s": the kept audio and the action log behind it, uploaded to the dev server. The
  // outcome shows beside the link for a few seconds; a page that has gone by then shows nothing.
  const onSaveCapture = useCallback(async () => {
    const current = stateRef.current;
    if (!current) {
      return;
    }
    clearTimeout(captureStatusTimerRef.current);
    setCaptureStatus('saving');
    let status: PitchCaptureStatus = 'saved';
    try {
      const saved = await savePitchCapture({ audio, state: current, entries: captureLogRef.current });
      if (!saved) {
        console.error('Pitch Match capture: no audio kept to save');
        status = 'failed';
      }
    } catch (error) {
      console.error('Pitch Match capture could not be saved', error);
      status = 'failed';
    }
    if (!mountedRef.current) {
      return;
    }
    setCaptureStatus(status);
    captureStatusTimerRef.current = setTimeout(() => setCaptureStatus(undefined), captureStatusShowMs);
  }, [audio]);

  const sessionLevel = state?.level ?? level;
  const title = `${learnModuleName(LearnModule.PitchMatch)} · ${learnLevelName(sessionLevel)}`;
  const after = recorded?.progress ?? progress;

  return (
    <OuterPageContent id={pitchMatchPageId} data-phase={state?.phase} data-mode={state?.mode ?? mode}>
      <Header title={title} onBack={() => void onBack()} />
      {!state && (
        <PitchStartScreen
          level={level}
          unlocked={unlocked}
          selectable={selectableLevels}
          singAlong={singAlong}
          onSelectLevel={setLevel}
          onToggleSingAlong={onToggleSingAlong}
          onStart={() => void beginSession()}
        />
      )}
      {state?.phase === 'note' && (
        <PitchNoteScreen
          state={state}
          clef={clef}
          onReplay={onReplay}
          onSkip={onSkip}
          // Asked on every render, and the page renders on every frame: the link appears with the
          // first audio kept
          onSaveCapture={captureAvailable && audio.hasCapture() ? () => void onSaveCapture() : undefined}
          captureStatus={captureStatus}
        />
      )}
      {state?.phase === 'failed' && (
        <SingingFailureScreen
          failure={state.failure ?? 'unknown'}
          idPrefix={pitchIdPrefix}
          backLabel={pitchMatchBack().label}
          onRetry={() => void onRetry()}
          onBack={() => void onBack()}
        />
      )}
      {state?.phase === 'summary' && (
        <LearnSummaryScreen
          idPrefix={pitchIdPrefix}
          moduleName={learnModuleName(LearnModule.PitchMatch)}
          level={state.level}
          score={sessionScore(state.results)}
          bestStreak={bestStreak(state.results)}
          squares={summarySquares(state.results)}
          module={moduleProgress(after, LearnModule.PitchMatch)}
          dayStreak={currentStreak(after, today)}
          isNewBest={recorded?.isNewBest ?? false}
          unlockedLevel={recorded?.unlockedLevel}
          onPlayAgain={() => void beginSession()}
          backLabel={pitchMatchBack().label}
          onBack={() => void onBack()}
        />
      )}
    </OuterPageContent>
  );
};
